import { describeError, useApi, useAuthState } from '@classgo/app-kit';
import { Badge, Button, Card, EmptyState, Input, Select, useToast } from '@classgo/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';

import { listQuery } from '../../academic/api';
import { config } from '../../config';
import { useT } from '../../i18n';
import { mentoriaQueries, openSession, streamTurn } from '../../mentoria/api';
import type { ChatMessage, MentoriaSession, StreamDone } from '../../mentoria/types';
import { recordQueries } from '../../records/api';
import { useTenant } from '../../tenant/tenant-provider';

const money = (usd: number) => `US$ ${usd.toFixed(4)}`;

/**
 * The MentorIA demo: pick a student, a subject and a topic of their grade, then talk to the
 * tutor. Answers stream in; the footer shows the model, tokens, cost and the remaining quota
 * (ADR-0009). Behind a feature flag until the mobile app and the activation flow exist.
 */
export const MentoriaScreen = () => {
  const t = useT();
  const api = useApi();
  const toast = useToast();
  const { sede } = useTenant();
  const { tokens, actAs } = useAuthState();
  const students = useQuery(recordQueries.students(api, { sedeId: sede.id, page: 1 }));
  const subjects = useQuery(listQuery(api, 'subjects'));
  const [studentId, setStudentId] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [topic, setTopic] = useState('');
  const student = students.data?.items.find((row) => row.id === studentId);
  const topics = useQuery(mentoriaQueries.topics(api, student?.gradeId ?? '', subjectId));
  const [session, setSession] = useState<MentoriaSession | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [last, setLast] = useState<StreamDone | null>(null);
  const [spent, setSpent] = useState(0);
  const bottom = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!studentId && students.data?.items[0]) setStudentId(students.data.items[0].id);
    if (!subjectId && subjects.data?.items[0]) setSubjectId(subjects.data.items[0].id);
  }, [studentId, subjectId, students.data, subjects.data]);
  useEffect(() => {
    if (!topic && topics.data?.[0]) setTopic(topics.data[0].topic);
  }, [topic, topics.data]);
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const open = useMutation({
    mutationFn: () => openSession(api, { studentUserId: studentId, subjectId, topic }),
    onSuccess: (created) => {
      setSession(created);
      setMessages([]);
      setLast(null);
      setSpent(0);
    },
    onError: (thrown) => toast.show({ tone: 'error', title: describeError(t, thrown) }),
  });

  /**
   * Sends one question and follows the answer. `retryId` reuses the bubble of a question that
   * failed, so retrying does not duplicate it in the conversation.
   */
  const ask = async (content: string, retryId?: string) => {
    if (!session || !content || streaming || !tokens) return;
    const stamp = Date.now();
    const userId = retryId ?? `u-${stamp}`;
    const assistantId = `a-${stamp}`;
    setMessages((current) => [
      ...(retryId
        ? current.map((message) =>
            message.id === retryId ? { ...message, status: 'sending' as const } : message,
          )
        : [...current, { id: userId, role: 'user' as const, content, status: 'sending' as const }]),
      { id: assistantId, role: 'assistant' as const, content: '', pending: true },
    ]);
    setStreaming(true);
    try {
      const done = await streamTurn({
        streamUrl: config.mentoria.streamUrl,
        token: tokens.idToken,
        actAsSession: actAs?.sessionId ?? null,
        sessionId: session.id,
        content,
        onDelta: (text) =>
          setMessages((current) =>
            current.map((message) =>
              message.id === assistantId
                ? { ...message, content: message.content + text }
                : message,
            ),
          ),
      });
      setLast(done);
      setSpent((value) => value + done.costUsd);
      setMessages((current) =>
        current.map((message) => {
          if (message.id === assistantId) return { ...message, pending: false };
          return message.id === userId ? { ...message, status: 'sent' as const } : message;
        }),
      );
    } catch (thrown) {
      const code = (thrown as { code?: string }).code;
      const title = code === 'FORBIDDEN' ? t('mentoria.quota.spent') : t('mentoria.error');
      toast.show({ tone: 'error', title, description: (thrown as Error).message });
      // The turn never reached the model: the question stays with its retry and the empty answer
      // goes away. Nothing was stored on the server either.
      setMessages((current) =>
        current
          .filter((message) => message.id !== assistantId)
          .map((message) =>
            message.id === userId ? { ...message, status: 'failed' as const } : message,
          ),
      );
    } finally {
      setStreaming(false);
    }
  };

  const send = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setDraft('');
    void ask(content);
  };

  if (!config.mentoria.enabled) {
    return <EmptyState title={t('mentoria.disabled')} />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[20rem_1fr]">
      <Card title={t('mentoria.context.title')}>
        <p className="mb-3 text-xs text-gray-500">{t('mentoria.context.help')}</p>
        <div className="space-y-3">
          <Select
            label={t('mentoria.field.student')}
            value={studentId}
            onChange={(event) => setStudentId(event.target.value)}
            options={(students.data?.items ?? []).slice(0, 50).map((row) => ({
              value: row.id,
              label: `${row.givenName} ${row.familyName ?? ''} · ${row.groupName ?? ''}`,
            }))}
          />
          <Select
            label={t('mentoria.field.subject')}
            value={subjectId}
            onChange={(event) => {
              setSubjectId(event.target.value);
              setTopic('');
            }}
            options={(subjects.data?.items ?? []).map((row) => ({
              value: row.id,
              label: row.name,
            }))}
          />
          <Select
            label={t('mentoria.field.topic')}
            value={topic}
            onChange={(event) => setTopic(event.target.value)}
            options={(topics.data ?? []).map((row) => ({ value: row.topic, label: row.topic }))}
            placeholder={(topics.data ?? []).length === 0 ? t('mentoria.noTopics') : undefined}
          />
          {topics.data?.find((row) => row.topic === topic)?.logro ? (
            <p className="text-xs text-gray-500">
              {topics.data.find((row) => row.topic === topic)?.logro}
            </p>
          ) : null}
          <Button
            className="w-full"
            loading={open.isPending}
            disabled={!studentId || !subjectId || !topic}
            onClick={() => open.mutate()}
          >
            {session ? t('mentoria.newSession') : t('mentoria.start')}
          </Button>
        </div>
      </Card>
      <Card title={t('mentoria.chat.title')} flush>
        {!session ? (
          <div className="p-6">
            <EmptyState
              title={t('mentoria.chat.empty.title')}
              description={t('mentoria.chat.empty.body')}
            />
          </div>
        ) : (
          <div className="flex h-[36rem] flex-col">
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-2 text-xs text-gray-500">
              <Badge tone="brand">{session.subjectName}</Badge>
              <span>{session.topic}</span>
              <span className="ml-auto">
                {t('mentoria.quota.remaining', {
                  n: Math.max(0, session.remainingQuota - spentTurns(messages)),
                })}
              </span>
            </div>
            <div
              className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
              aria-live="polite"
              aria-label={t('mentoria.chat.title')}
            >
              {messages.length === 0 ? (
                <p className="text-sm text-gray-500">
                  {t('mentoria.chat.hint', { name: student?.givenName ?? '' })}
                </p>
              ) : null}
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={
                    message.role === 'user'
                      ? 'flex flex-col items-end gap-1'
                      : 'flex flex-col items-start gap-1'
                  }
                >
                  <div
                    className={`max-w-[80%] whitespace-pre-wrap rounded-card px-3 py-2 text-sm ${
                      message.role === 'user'
                        ? message.status === 'failed'
                          ? 'bg-danger-100 text-danger-700 ring-1 ring-danger-600/30'
                          : `bg-brand-600 text-white ${message.status === 'sending' ? 'opacity-70' : ''}`
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    {message.content || (message.pending ? <TypingDots /> : '')}
                  </div>
                  {message.role === 'user' && message.status && message.status !== 'sent' ? (
                    <p className="flex items-center gap-2 text-xs text-gray-500">
                      {message.status === 'sending' ? (
                        t('mentoria.chat.sending')
                      ) : (
                        <>
                          <span className="text-danger-700">{t('mentoria.chat.failed')}</span>
                          <button
                            type="button"
                            className="font-medium text-brand-700 underline underline-offset-2 hover:decoration-2 disabled:text-gray-400"
                            disabled={streaming}
                            onClick={() => void ask(message.content, message.id)}
                          >
                            {t('mentoria.chat.retry')}
                          </button>
                        </>
                      )}
                    </p>
                  ) : null}
                </div>
              ))}
              <div ref={bottom} />
            </div>
            <form onSubmit={send} className="flex gap-2 border-t border-gray-200 p-3">
              <div className="flex-1">
                <Input
                  label={t('mentoria.chat.input')}
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  disabled={streaming}
                  autoComplete="off"
                  maxLength={2000}
                />
              </div>
              <Button
                type="submit"
                className="self-end"
                loading={streaming}
                disabled={!draft.trim()}
              >
                {t('mentoria.chat.send')}
              </Button>
            </form>
            <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
              {t('mentoria.footer', {
                model: session.modelLabel ?? session.modelId,
                tokens: last ? `${last.usage.inputTokens} + ${last.usage.outputTokens}` : '—',
                latency: last ? `${(last.usage.latencyMs / 1000).toFixed(1)} s` : '—',
                cost: money(spent),
              })}
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

/**
 * The three dots while MentorIA writes: the same signal every chat gives.
 */
const TypingDots = () => (
  <span className="flex items-center gap-1 py-1" aria-label="Escribiendo">
    {[0, 150, 300].map((delay) => (
      <span
        key={delay}
        className="h-1.5 w-1.5 animate-bounce rounded-full bg-gray-400"
        style={{ animationDelay: `${delay}ms` }}
      />
    ))}
  </span>
);

const spentTurns = (messages: ChatMessage[]) =>
  messages.filter((message) => message.role === 'user' && message.status !== 'failed').length;
